/*******************************************************************************
Chemical Application Actions: All interactions in the chemical component

*******************************************************************************/
import { ADD_SOLUTION, EDIT_SOLUTION, DELETE_SOLUTION, SELECT_SOLUTION_TO_EDIT,
  SET_TOTAL_GALLONS, PREPARE_DATA, FETCH_CHEMICAL_APPLICATIONS, SELECT_FIELD,
  DELETE_CHEMICAL_APPLICATION, SELECT_FIELD_FOR_EDIT, SELECT_CHEMICAL_APPLICATION_TO_EDIT,
  SELECT_APPLICATION_TO_VIEW
} from './types';
import { ROOT_URL } from '../../constants';
import { fillForm, resetForm, generateID, calculatePercentage, acresBalance,
  calculateHoursFromTime, findEmployeeByName, checkApplicationForm } from '../cult_utils';
import { error, success } from './chem_actions';
import axios from 'axios';
const $ = window.$;

/**
* fetchChemicalApplications(): Fetches all chemical applications
* @returns {Array} chemicals
* @called-in: ['/components/chemical/chemical_app_view.js']
**/
export function fetchChemicalApplications() {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchChemicalApplications`, {
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
        $('#preloader').css('display','none');
        dispatch({ type: FETCH_CHEMICAL_APPLICATIONS, payload: res.data.chemApps });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error fetching chemicals.`));
      });
  }
}
/**
* selectApplicationToView(): Sets the application to view
* @param {Object} app: app to view
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_view.js->chem_app_table.js->fab_modal.js->chem_app_display.js']
**/
export function selectApplicationToView(app) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchPreviousApplication`, {
      params: { date: app.date, field: app.fieldId, round: app.round },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
      dispatch({
        type: SELECT_APPLICATION_TO_VIEW,
        payload: app,
        previousApplications: res.data.records.filter((s) => s.id !== app.id)
      });
    }).catch((err) => {
      $('#preloader').css('display','none');
      dispatch({type: 'ERROR', payload: err});
    });
  }
}
/**
* createChemicalApplication(): Submits a new chemical
* @param {Array} formData: All form data
* @returns {Object} Newly created chemical
* @called-in: ['/components/chemical/chemical_app_create']
**/
export function createOrEditChemicalApplication({ data, type, path }) {
  // Form validation check
  if (checkApplicationForm(data)) return error('Entire form must be filled.');
  if (data.solutions.length === 0) return error('At least one chemical is required.');
  if (Number(data.measurement) === 0) return error('Gallons used must be greater than 0.');
  if (Number(data.acresBalance) < 0) return error('Cannot exceed total acres in a field. Re-enter Acres Completed.');
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/${path}`, { data })
      .then((res) => {
        $('#preloader').css('display','none');
        resetForm('chemical-application-form');
        success(res.data.msg);
        dispatch({ type, payload: res.data.record });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch({type: 'ERROR', payload: err});
      });
  }
}
/**
* deleteChemicalApplication(): Delete an existing chemical
* @param {Array} data: id of application; ids of chemicals applied
* @returns {Object} Newly created chemical
* @called-in: ['/components/chem_app/chem_app_view.js->chem_app_table.js->fab_modal.js->chem_app_display.js']
**/
export function deleteChemicalApplication(data) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/deleteChemicalApplication`, { data: { id: data.id, solutions: data.solutions.map((s) => s.id) } })
      .then((res) => {
        $('#preloader').css('display','none');
        success(res.data.msg);
        dispatch({ type: DELETE_CHEMICAL_APPLICATION, payload: res.data.id });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch({type: 'ERROR', payload: err});
      });
  }
}

/**
* prepareData(): Prepares form data for submission
* @param {Object} form: all form data
* @param {Array} fields: all fields from a location
* @param {Array} employees: list of cultivation employees
* @param {Array} solutions: chemical cocktail array
* @param {String} submitter: submitter eid of form
* @param {String} certId: cert# to use regulated chemicals
* @returns {Object} type, payload
* @called-in: ['/components/chemApp/chem_app_create.js']
**/
export function prepareData({ user, form, selectedField, employees, solutions, deletedSolutions, submitter, certId, chemAppId, editingApplication }) {
  let e = findEmployeeByName(form.employee, employees);
  // If EID is filled in form
  if (!e && form.employee !== '') e = employees.find((emp) => emp.eid === form.employee);
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchPreviousApplication`, {
      params: { date: form.date, field: form.fieldId, round: form.round },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
      // Determine remaining balance of acres
      let previousAcresWorked = res.data.records.filter((d) => d.chemAppId !== chemAppId).reduce((acc, cur) => { return acc + Number(cur.acresCompleted) },0);
      //if (editingApplication) previousAcresWorked = 0;
      let totalAcresWorked = Number(form.acresCompleted) + previousAcresWorked;
      let balance = acresBalance(totalAcresWorked, selectedField.totalAcre);

      dispatch({
        type: PREPARE_DATA,
        payload: {
          ...form,
          hours: calculateHoursFromTime({ date: form.date, startTime: form.startTime, endTime: form.endTime }),
          employee: e.eid,
          chemAppId: chemAppId ? chemAppId : generateID(),
          certId,
          submitter,
          acresBalance: balance,
          reiDaysRemaining: Math.max(...solutions.map((s) => s.rei)),
          preHarvestDaysRemaining: Math.max(...solutions.map((s) => s.preHarvest)),
          canEdit: 1,
          isComplete: form.isComplete === 'Incomplete' ? 0 : 1,
          solutions,
          deletedSolutions,
          editedBy: editingApplication ? `${user.firstName} ${user.lastName}` : null
        },
        previousApplications: res.data.records
      });
    }).catch((err) => {
      $('#preloader').css('display','none');
      dispatch({type: 'ERROR', payload: err});
    });
  }
}
/**
* addSolution(): Adds a solution in the create application form
* @param {Object} solution: chemical used form info
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_create.js']
**/
export function addSolution({ solution, totalGallons, formId, chemicals }) {
  let { chemId, chemDosage1, chemDosage2 } = solution;
  if (chemId === '') return error('All fields must be filled.');
  if (chemDosage1 === '' || Number(chemDosage1) <= 0) return error('Must be greater than 0.');
  if (chemDosage2 === '') return error('Select a unit of measure');

  let chem = chemicals.find((c) => c.id === Number(chemId));
  resetForm(formId);
  return {
    type: ADD_SOLUTION,
    payload: {
      id: generateID(),
      percentageUsed: calculatePercentage({ quantity: solution.chemDosage1, unit: solution.chemDosage2, totalGallons: totalGallons }),
      ...solution,
      rei: chem.rei,
      preHarvest: chem.preHarvestInterval
    }
  }
}
/**
* selectSolutionToEdit(): Selects a solution to edit in the create application form
* @param {Object} solution: chemical used form info
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_create.js']
**/
export function selectSolutionToEdit(solution, formId='') {
  setTimeout(() => {
    fillForm(formId, solution);
  }, 200);
  return {
    type: SELECT_SOLUTION_TO_EDIT,
    payload: solution
  }
}
/**
* editSolution(): Edits a solution added in the create application form
* @param {Object} solution: chemical used form info
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_create.js']
**/
export function editSolution({ solution, editSolution, totalGallons, formId, chemicals }) {
  let { chemId, chemDosage1, chemDosage2 } = solution;
  if (chemId === '') return error('All fields must be filled.');
  if (chemDosage1 === '' || Number(chemDosage1) <= 0) return error('Must be greater than 0.');
  if (chemDosage2 === '') return error('Select a unit of measure');
  let chem = chemicals.find((c) => c.id === Number(chemId));
  resetForm(formId);
  return {
    type: EDIT_SOLUTION,
    payload: {
      id: editSolution.id ? editSolution.id : generateID(),
      percentageUsed: calculatePercentage({ quantity: solution.chemDosage1, unit: solution.chemDosage2, totalGallons: totalGallons }),
      ...solution,
      rei: chem.rei,
      preHarvest: chem.preHarvestInterval
    }
  }
}
/**
* deleteSolution(): Deletes a solution added in the create application form
* @param {Object} solution: chemical used form info
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_create.js']
**/
export function deleteSolution(solution) {
  return {
    type: DELETE_SOLUTION,
    payload: solution
  }
}
/**
* setTotalGallons(): Sets the total gallons of solution
* @param {Object} val: total gallons
* @returns {Array} type, payload
* @called-in: ['/components/chem-app/chem_app_create.js->chem_app_form.js']
**/
export function setTotalGallons(val) {
  return {
    type: SET_TOTAL_GALLONS,
    payload: Number(val)
  }
}
/**
* selectField(): Selects a field from field list
* @param {String} field: the selected field
* @returns {Object} type, payload: action and the field string
* @called-in: ['/components/chem_app/chem_app_create.js->chem_app_form.js']
* @notes: Call in onComplete and onBlur; make sure users cannot select a field outside of a correct location
**/
export function selectField(fields, val, id, editChem=false) {
  if (fields.length === 0) return { type: 'DUMMY' };
  let field = fields.find((f) => f.field === val);
  if (field === undefined) {
    document.getElementById(id).value = '';
    return { type: 'DUMMY' };
  }
  if (editChem) {
    return {
      type: SELECT_FIELD_FOR_EDIT,
      payload: field
    };
  }
  return {
    type: SELECT_FIELD,
    payload: field
  };
}
/**
* selectChemAppToEdit(): Selects a application to edit
* @param {String} formId: the id of the form
* @param {String} chem: the id of the form
* @param {String} formId: the id of the form
* @param {String} formId: the id of the form
* @returns {Object} type, payload: action and the field string
* @called-in: ['/components/chem_app/chem_app_create.js->chem_app_form.js']
* onEdit: mutate solutions to reflect initial state with chemDosage split into number and unit of measure
**/
export function selectChemAppToEdit({ formId, chem, chemicalList, employees }) {
  let { solutions } = chem;
  let employee = employees.find((e) => e.eid === chem.employee);

  let _solutions = solutions.map((s) => {
    if (s.hasOwnProperty('chemDosage')) {
      let _chem = chemicalList.find((c) => c.id === Number(s.chemId));
      let c = s.chemDosage.split(' ');
      s.chemDosage1 = c[0];
      s.chemDosage2 = c[1];
      s.rei = _chem.rei;
      s.preHarvest = _chem.preHarvestInterval;
      delete s.chemDosage;
      return s;
    }
    return s;
  });
  chem.solutions = _solutions;
  // Display employee name in Employee Autocomplete rather than employee ID
  if (employee) chem.employee = `${employee.firstName} ${employee.lastName}`;
  // Fill form
  fillForm(formId, chem);
  // After form is filled, set employee back to employee id
  chem.employee = employee.eid;
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchField`, {
      params: { field: chem.fieldId },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
        dispatch({
          type: SELECT_CHEMICAL_APPLICATION_TO_EDIT,
          payload: chem,
          field: res.data.field
        });
      }).catch((err) => {
        console.log(err)
        dispatch(error(`There was an error fetching the field for the application.`))
      });
  }
}





/* END */



// WEBPACK FOOTER //
// ./src/cultivation/actions/chem_app_actions.js